import { BUSINESS_TIME_ZONE, businessToday } from "@/lib/businessDate";
import { localeFor, type SupportedLanguage } from "@/lib/i18n/languages";

const DAY_MS = 86_400_000;

/** YYYY-MM-DD in Europe/Berlin — the day an acknowledgement belongs to. */
export function berlinDateString(now: Date): string {
  return businessToday(now);
}

function parse(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Whole days from `fromIso` to `now`; unknown or future → 0. */
export function daysBetween(
  fromIso: string | null | undefined,
  now: Date
): number {
  const t = parse(fromIso);
  if (t == null) return 0;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

/**
 * Whole Berlin calendar days from today to `iso` (negative = past); unknown → null.
 * A bare YYYY-MM-DD parses as UTC midnight, which is the same day in Berlin.
 */
export function daysUntil(
  iso: string | null | undefined,
  now: Date
): number | null {
  const t = parse(iso);
  if (t == null) return null;
  const today = Date.parse(berlinDateString(now));
  const day = Date.parse(berlinDateString(new Date(t)));
  return Math.round((day - today) / DAY_MS);
}

/**
 * A card date in the reader's language: "09 Oct 2026" / "09. Okt. 2026".
 * A bare YYYY-MM-DD is a calendar date, shown as written (no zone shift); a
 * timestamp is shown as its Berlin day. Unparseable input comes back as is.
 */
/** "2026-07" → "July 2026" / "Juli 2026"; anything else unchanged. */
export function formatBrainMonth(
  value: string,
  language: SupportedLanguage
): string {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return value;
  return new Intl.DateTimeFormat(localeFor(language), {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
}

export function formatBrainEur(n: number, language: SupportedLanguage): string {
  return new Intl.NumberFormat(localeFor(language), {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

export function formatBrainDate(
  value: string,
  language: SupportedLanguage
): string {
  const plain = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = new Date(plain ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat(localeFor(language), {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: plain ? "UTC" : BUSINESS_TIME_ZONE,
  }).format(d);
}
